import { Outlet } from 'react-router';

import {
  deviceInnerRoutes,
  deviceRouteHandle,
} from '@/console/app/routes/devices/routes';
import {
  documentInnerRoutes,
  documentsRouteHandle,
} from '@/console/app/routes/documents/routes';
import {
  gatewayInnerRoutes,
  gatewayRouteHandle,
} from '@/console/app/routes/gateways/routes';
import {
  pipelineInnerRoutes,
  pipelinesRoutesHandle,
} from '@/console/app/routes/pipelines/routes';
import {
  projectInnerRoutes,
  projectRouteHandle,
} from '@/console/app/routes/projects/routes';
import {
  roleInnerRoutes,
  rolesRouteHandle,
} from '@/console/app/routes/roles/routes';
import {
  innerVaultRoutes,
  vaultRouteHandle,
} from '@/console/app/routes/security-hub/routes';
import {
  userInnerRoutes,
  userRouteHandle,
} from '@/console/app/routes/users/routes';
import { TCustomRouteObject } from '@/shared/types/router.types';

const groupedDeviceRoutes: TCustomRouteObject = {
  path: 'devices',
  Component: () => <Outlet />,
  handle: deviceRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupDevicesRoute } = await import('./group');
        return { Component: GroupDevicesRoute };
      },
    },
    ...deviceInnerRoutes,
  ],
};

const groupedProjectRoutes: TCustomRouteObject = {
  path: 'projects',
  Component: () => <Outlet />,
  handle: projectRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupProjectsRoute } = await import('./group');
        return { Component: GroupProjectsRoute };
      },
    },
    ...projectInnerRoutes,
  ],
};

const groupedGatewayRoutes: TCustomRouteObject = {
  path: 'gateways',
  Component: () => <Outlet />,
  handle: gatewayRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupGatewaysRoute } = await import('./group');
        return { Component: GroupGatewaysRoute };
      },
    },
    ...gatewayInnerRoutes,
  ],
};

const groupedUsersRoute: TCustomRouteObject = {
  path: 'users',
  Component: () => <Outlet />,
  handle: userRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupUsersRoute } = await import('./group');
        return { Component: GroupUsersRoute };
      },
    },
    ...userInnerRoutes,
  ],
};

const groupedRolesRoute: TCustomRouteObject = {
  path: 'roles',
  Component: () => <Outlet />,
  handle: rolesRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupRolesRoute } = await import('./group');
        return { Component: GroupRolesRoute };
      },
    },
    ...roleInnerRoutes,
  ],
};

const groupedVaultsRoute: TCustomRouteObject = {
  path: 'vaults',
  Component: () => <Outlet />,
  handle: vaultRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupVaultsRoute } = await import('./group');
        return { Component: GroupVaultsRoute };
      },
    },
    ...innerVaultRoutes,
  ],
};

const groupedDocumentsRoute: TCustomRouteObject = {
  path: 'documents',
  Component: () => <Outlet />,
  handle: documentsRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupDocumentsRoute } = await import('./group');
        return { Component: GroupDocumentsRoute };
      },
    },
    ...documentInnerRoutes,
  ],
};

const groupedLicensesRoute: TCustomRouteObject = {
  path: 'licenses',
  handle: {
    crumb: {
      label: 'licenses',
      localized: true,
    },
    permission: { action: 'license:readLicense' },
  },
  lazy: async () => {
    const { GroupLicensesRoute } = await import('./group');
    return { Component: GroupLicensesRoute };
  },
};

const groupedIntegrationsRoute: TCustomRouteObject = {
  path: 'integrations',
  handle: {
    crumb: {
      label: 'integrations',
      localized: true,
    },
  },
  lazy: async () => {
    const { GroupIntegrationsRoute } = await import('./group');
    return { Component: GroupIntegrationsRoute };
  },
};

const groupedPipelinesRoute: TCustomRouteObject = {
  path: 'pipelines',
  Component: () => <Outlet />,
  handle: pipelinesRoutesHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { GroupPipelinesRoute } = await import('./group');
        return { Component: GroupPipelinesRoute };
      },
    },
    ...pipelineInnerRoutes,
  ],
};

const subGroupsRoute: TCustomRouteObject = {
  path: 'subgroups',
  handle: {
    crumb: {
      label: 'subgroups',
      localized: true,
    },
  },
  lazy: async () => {
    const { SubgroupsRoute } = await import('./group');
    return { Component: SubgroupsRoute };
  },
};

export const groupInnerRoutes: TCustomRouteObject[] = [
  groupedDeviceRoutes,
  groupedProjectRoutes,
  groupedGatewayRoutes,
  groupedUsersRoute,
  groupedRolesRoute,
  groupedVaultsRoute,
  groupedDocumentsRoute,
  groupedPipelinesRoute,
  groupedLicensesRoute,
  groupedIntegrationsRoute,
  subGroupsRoute,
];
